<script>
/**
 * Renders OpenFreeMap vector tiles inside a vue2-leaflet <LMap>.
 *
 * OpenFreeMap (https://openfreemap.org) only serves vector tiles, which
 * Leaflet cannot draw by itself, so this component mounts a MapLibre GL
 * map into the Leaflet tile pane via @maplibre/maplibre-gl-leaflet.
 * Leaflet keeps handling panning, zooming and markers; MapLibre only paints
 * the basemap underneath. No API key is required.
 *
 * Usage: place it where an <LTileLayer> used to be, directly inside <LMap>.
 */
// The UMD build is imported explicitly: webpack 4 cannot consume the package's .mjs entry.
import { maplibreGL } from '@maplibre/maplibre-gl-leaflet/leaflet-maplibre-gl.js'
import * as Sentry from '@sentry/browser'
// Leaflet's own CSS sizes the map and stacks its panes (tiles under overlays). It lived in
// Ad.vue's <style>, which reached every page only while Ad.vue was in the main bundle; since
// routes are lazy (053aba1) a map opened before any ad page had no Leaflet CSS (2026-10-14:
// /onskemal map over the sidebar, GeoJSON under the basemap). Every map renders this layer.
import 'leaflet/dist/leaflet.css'
import 'maplibre-gl/dist/maplibre-gl.css'

// Required by OpenFreeMap's terms of use. Set explicitly so it does not depend
// on the TileJSON having loaded before Leaflet reads the layer's attribution.
export const OPENFREEMAP_ATTRIBUTION =
  '<a href="https://openfreemap.org" target="_blank">OpenFreeMap</a> ' +
  '<a href="https://www.openmaptiles.org/" target="_blank">&copy; OpenMapTiles</a> ' +
  'Data from <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>'

export const OPENFREEMAP_STYLES = {
  liberty: 'https://tiles.openfreemap.org/styles/liberty',
  bright: 'https://tiles.openfreemap.org/styles/bright',
  positron: 'https://tiles.openfreemap.org/styles/positron'
}

export default {
  name: 'OpenFreeMapLayer',
  props: {
    // Either a key of OPENFREEMAP_STYLES or a full style URL.
    mapStyle: {
      type: String,
      default: 'bright'
    }
  },
  computed: {
    styleUrl() {
      return OPENFREEMAP_STYLES[this.mapStyle] || this.mapStyle
    }
  },
  watch: {
    styleUrl(url) {
      if (this.mapObject && this.mapObject.getMaplibreMap()) {
        this.mapObject.getMaplibreMap().setStyle(url)
      }
    }
  },
  mounted() {
    // <LMap> only renders its slot once the Leaflet map exists, so the
    // nearest ancestor with a mapObject is our map.
    const map = this.findLeafletMap()
    if (!map) {
      console.warn('OpenFreeMapLayer must be placed inside an <LMap>')
      return
    }
    // Kept off `data` on purpose: Leaflet/MapLibre objects must not be reactive.
    this.leafletMap = map
    this.mapObject = maplibreGL({
      style: this.styleUrl,
      attributionControl: { customAttribution: OPENFREEMAP_ATTRIBUTION }
    })

    // Guard against an unbounded error loop in @maplibre/maplibre-gl-leaflet.
    //
    // Leaflet binds a layer's getEvents() handlers *before* calling onAdd()
    // (Layer._layerAdd), and the only thing that unbinds them is the layer's
    // own 'remove' event. The bridge creates its MapLibre map inside onAdd
    // (_initGL). So if `new maplibre_gl.Map()` throws - most commonly because
    // the browser will not hand out a WebGL context - `_glMap` is never
    // assigned, the layer never finishes being added, 'remove' never fires,
    // and the still-bound `move` handler runs `this._glMap.jumpTo(...)` on
    // every single map movement, forever. One desktop tab produced 239k
    // exceptions this way (rate-limited by posthog-js to 10/s).
    //
    // The bridge's _update guards `this._map` but not `this._glMap`; this
    // adds the missing check on our instance only.
    const originalUpdate = this.mapObject._update
    this.mapObject._update = function (e) {
      if (!this._glMap) return
      return originalUpdate.call(this, e)
    }

    try {
      this.mapObject.addTo(map)
    } catch (err) {
      // Report once. Without this the failure is silent: the basemap simply
      // never paints and the page looks merely broken rather than errored.
      console.error('[OpenFreeMapLayer] MapLibre failed to initialise', err)
      Sentry.captureException(err)
      if (this.$posthog) {
        this.$posthog.captureException(err)
      }
      // Detach so no handler survives the failed initialisation.
      try {
        if (map.hasLayer(this.mapObject)) {
          map.removeLayer(this.mapObject)
        }
      } catch (removeErr) {
        console.error('[OpenFreeMapLayer] cleanup after failed init', removeErr)
      }
      this.mapObject = null
    }
  },
  beforeDestroy() {
    // When the whole <LMap> is torn down Leaflet has already removed us,
    // so only remove the layer if it is still attached.
    if (this.leafletMap && this.mapObject && this.leafletMap.hasLayer(this.mapObject)) {
      this.leafletMap.removeLayer(this.mapObject)
    }
    this.mapObject = null
    this.leafletMap = null
  },
  methods: {
    // Resolves once MapLibre has finished drawing every visible tile at the
    // current view. Resolves straight away if MapLibre never initialised.
    whenIdle() {
      const glMap = this.mapObject && this.mapObject.getMaplibreMap()
      if (!glMap) {
        return Promise.resolve()
      }
      return new Promise(resolve => {
        glMap.once('idle', resolve)
        // 'idle' only fires after a render, so request one in case the map
        // has nothing left to do.
        glMap.triggerRepaint()
      })
    },
    findLeafletMap() {
      let parent = this.$parent
      while (parent && !parent.mapObject) {
        parent = parent.$parent
      }
      return parent ? parent.mapObject : null
    }
  },
  render(h) {
    return h()
  }
}
</script>
